<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Stack, Input, Select, Text, Collapse, Button } from '@Shelly/shelly-design-system';
	import { rooms } from '../../../stores';
	import { sceneMetadata } from '../../../stores/flow_editor';
	import ActiveTimePreviewWithControls from '../../Scenes/SceneWizard/Steps/components/ActiveTimePreviewWithControls.svelte';
	import AddEditInterval from '../../Scenes/SceneWizard/Process/ActiveTime/AddEditInterval.svelte';
	import {
		extractEmptyInterval,
		fillIntervalMatrix,
		findEmptyIntervals,
	} from '../../Scenes/helpers/intervals_helpers';
	import { calculateWhenEnd } from '../../../helpers/interface_helpers';
	import { real_duration_from_negative, _calculate_duration } from '../../../helpers/utilities';
	import IconSearchModal from '../../Lists/IconSearchModal.svelte';

	/** When true, styles for embedding under the flow inspector header (no extra outer title). */
	export let embedded: boolean = false;

	type IndailyInner = { dows: string; startat: string; dur: number };

	/** Stored in scene metadata / merged as `if.and` `{ indailyint: IndailyInner[] }` — flat inner objects only. */
	function normalizeToInner(raw: any): IndailyInner {
		if (raw && typeof raw === 'object' && raw.indailyint?.dows != null) {
			const i = raw.indailyint;
			return {
				dows: String(i.dows).trim(),
				startat: String(i.startat || '00:00').slice(0, 5),
				dur: Math.max(1, Number(i.dur) || 1),
			};
		}
		if (raw?.dows != null && raw?.startat != null) {
			return {
				dows: String(raw.dows).trim(),
				startat: String(raw.startat).slice(0, 5),
				dur: Math.max(1, Number(raw.dur) || 1),
			};
		}
		/** Legacy flow inspector: JS weekday ids Sun=0, Mon=1 … Sat=6 */
		const from = typeof raw?.from === 'string' && raw.from ? raw.from.slice(0, 5) : '08:00';
		const to = typeof raw?.to === 'string' && raw.to ? raw.to.slice(0, 5) : '18:00';
		let durRaw = _calculate_duration([from, to]);
		const dur =
			durRaw < 0
				? real_duration_from_negative(durRaw)
				: Math.max(1, Math.min(1439, durRaw || 600));
		const days: number[] = Array.isArray(raw?.days)
			? raw.days.map((d: unknown) => Number(d)).filter((d) => !Number.isNaN(d) && d >= 0 && d <= 6)
			: [1, 2, 3, 4, 5];
		const isins = [...new Set(days.map((d) => (d === 0 ? 6 : d - 1)))].sort((a, b) => a - b);
		const dows = isins.map((el) => Number(el) + 1).join(' ');
		return { dows, startat: from, dur };
	}

	function wrap(inner: IndailyInner) {
		return { indailyint: { ...inner, dows: inner.dows.trim() } };
	}

	$: name = $sceneMetadata.name;
	$: selectedRoom =
		$sceneMetadata.room == null || $sceneMetadata.room === 0 || $sceneMetadata.room === -1
			? -1
			: Number($sceneMetadata.room);

	$: innerIntervals = ($sceneMetadata.activeTimeIntervals ?? []).map((raw) => normalizeToInner(raw));

	$: intervalsWrapped = innerIntervals.map((i) => wrap(i));

	$: intervalMatrix =
		innerIntervals.length > 0 ? fillIntervalMatrix(intervalsWrapped, 'scene_active_time') : null;
	$: emptyHoursForWeek = intervalMatrix ? findEmptyIntervals(intervalMatrix) : {};
	$: first_empty =
		innerIntervals.length > 0 && intervalMatrix
			? extractEmptyInterval(emptyHoursForWeek)
			: { isin: [0, 1, 2, 3, 4], time: ['08:00', '18:00'] };

	$: addDisabled =
		innerIntervals.length > 0 &&
		(!first_empty?.isin?.length || !first_empty?.time?.length || first_empty.time.length < 2);

	let activeTimeExpanded = false;
	let showIconSearchModal = false;
	let showEditActiveTime = false;
	let interval: {
		type: 'add' | 'edit';
		index: number;
		isin: number[];
		time: string[];
	} = {
		type: 'add',
		index: 0,
		isin: [0, 1, 2, 3, 4],
		time: ['08:00', '18:00'],
	};

	function commitIntervals(next: IndailyInner[]) {
		sceneMetadata.update((m) => ({
			...m,
			activeTimeIntervals: next.map((it) => ({
				dows: it.dows,
				startat: it.startat,
				dur: it.dur,
			})),
		}));
	}

	function handleNameChange(event: Event) {
		const value = (event.target as HTMLInputElement)?.value ?? '';
		sceneMetadata.update((m) => ({ ...m, name: value }));
	}

	$: flowIcon = String($sceneMetadata.image || 'fa-diagram-project');
	$: flowIconClass = `fas ${flowIcon}`;

	function handleIconSelected(event: CustomEvent<{ icon: string }>) {
		const icon = String(event.detail?.icon || '').trim();
		if (!icon) return;
		sceneMetadata.update((m) => ({ ...m, image: icon }));
		showIconSearchModal = false;
	}

	function resetFlowIcon() {
		sceneMetadata.update((m) => ({ ...m, image: 'fa-diagram-project' }));
	}

	$: roomOptions = Object.entries($rooms || {}).map(([id, room]) => {
		const r = room as { name?: string };
		return {
			value: parseInt(id, 10),
			title: r?.name || `Room ${id}`,
		};
	});

	$: allRoomOptions = [
		{ value: -1, title: $format('label_no_room', { default: 'No room' }) },
		...roomOptions,
	];

	$: selectedRoomOption =
		allRoomOptions.find(
			(o) => o.value === selectedRoom || String(o.value) === String(selectedRoom),
		) ?? allRoomOptions[0];

	function handleRoomChange(event: CustomEvent) {
		const detail = event.detail;
		const raw =
			typeof detail === 'string' || typeof detail === 'number' ? detail : (detail?.value ?? -1);
		const roomId = Number(raw);
		const room =
			roomId !== -1 && roomId !== 0
				? (($rooms as any)[roomId] ?? ($rooms as any)[String(roomId)])
				: null;
		sceneMetadata.update((m) => ({
			...m,
			room: roomId,
			roomName: room?.name || '',
		}));
	}

	function handleAdd() {
		if (innerIntervals.length === 0) {
			interval = {
				type: 'add',
				index: 0,
				isin: [0, 1, 2, 3, 4],
				time: ['08:00', '18:00'],
			};
		} else {
			interval = {
				type: 'add',
				index: innerIntervals.length,
				isin: first_empty?.isin ?? [],
				time: first_empty?.time ?? ['08:00', '18:00'],
			};
		}
		showEditActiveTime = true;
		activeTimeExpanded = true;
	}

	function handleEdit(active_time: { indailyint: IndailyInner }, ind: number) {
		interval = {
			type: 'edit',
			index: ind,
			isin: active_time.indailyint.dows.split(' ').map((e) => Number(e) - 1),
			time: [
				active_time.indailyint.startat,
				calculateWhenEnd(active_time.indailyint.startat, active_time.indailyint.dur),
			],
		};
		showEditActiveTime = true;
		activeTimeExpanded = true;
	}

	function handleDelete(ind: number) {
		const next = innerIntervals.filter((_, i) => i !== ind);
		commitIntervals(next);
	}

	function handleCancelModal() {
		showEditActiveTime = false;
	}

	function handleSaveInterval(e: CustomEvent<{ interval: typeof interval }>) {
		const saved = e.detail.interval;
		let interval_duration = _calculate_duration(saved.time);
		const inner: IndailyInner = {
			dows: saved.isin.map((el) => Number(el) + 1).join(' '),
			startat: saved.time[0],
			dur:
				interval_duration < 0
					? real_duration_from_negative(interval_duration)
					: interval_duration,
		};
		let next: IndailyInner[];
		if (saved.type === 'add') {
			next = [...innerIntervals, inner];
		} else {
			next = innerIntervals.map((it, i) => (i === saved.index ? inner : it));
		}
		commitIntervals(next);
		showEditActiveTime = false;
	}
</script>

<div class="scene-metadata-inspector" class:embedded>
	{#if !embedded}
		<div class="inspector-header">
			<h3>{$format('flow_scene_settings', { default: 'Flow settings' })}</h3>
		</div>
	{/if}

	<Stack direction="row" gap="m">
		<Stack direction="row" gap="m" class="flow-meta-top-row">
			<Input
				id="scene-name"
				aria-label="flow-name"
				label={$format('label_scene_name', { default: 'Flow name' })}
				placeholder={$format('placeholder_enter_scene_name', { default: 'Enter Flow name' })}
				value={name}
				on:input={handleNameChange}
				required={true}
			/>

			<Select
				id="scene-room"
				icon=""
				aria-label={$format('label_room', { default: 'Room' })}
				label={$format('label_room', { default: 'Room' })}
				options={allRoomOptions}
				value={selectedRoomOption}
				on:change={handleRoomChange}
			/>

			<div class="flow-icon-setting">
				<span class="flow-icon-label">Flow icon</span>
				<div class="flow-icon-controls">
					<span
						class="flow-icon-preview"
						style:background={$sceneMetadata.backgroundColor || 'rgb(102, 96, 237)'}
						aria-hidden="true"
					>
						<i class={flowIconClass}></i>
					</span>
					<Button
						variant="secondary"
						size="m"
						icon="fas fa-icons"
						on:click={() => (showIconSearchModal = true)}
					>
						Change icon
					</Button>
					{#if flowIcon !== 'fa-diagram-project'}
						<Button variant="secondary" size="m" on:click={resetFlowIcon}>Reset</Button>
					{/if}
				</div>
			</div>
		</Stack>

		<Collapse
			id="scene-active-time-windows"
			title={$format('label_active_time', { default: 'Active Time Windows' })}
			expanded={activeTimeExpanded}
			on:click={() => {
				activeTimeExpanded = !activeTimeExpanded;
			}}
		>
			<Stack direction="row" gap="m">
				<Text variant="secondary" size="sm">
					{$format('flow_active_time_scene_hint', {
						default: 'Restrict when this flow may run (applies to the whole automation).',
					})}
				</Text>

				{#if innerIntervals.length === 0}
					<Text variant="secondary" size="sm">
						{$format('message_no_active_time', {
							default: 'No time restrictions. This Flow can run anytime.',
						})}
					</Text>
				{:else}
					<Stack direction="column" gap="s">
						{#each intervalsWrapped as active_time, ind (ind)}
							<ActiveTimePreviewWithControls
								{active_time}
								disableDelete={false}
								on:edit={() => handleEdit(active_time, ind)}
								on:delete={() => handleDelete(ind)}
							/>
						{/each}
					</Stack>
				{/if}

				<Button variant="secondary" size="m" disabled={addDisabled} on:click={handleAdd}>
					<i class="fa fa-plus"></i>
					{$format('button_add_interval', { default: 'Add interval' })}
				</Button>
			</Stack>
		</Collapse>
	</Stack>
</div>

{#if showEditActiveTime}
	<AddEditInterval
		intervals={intervalsWrapped}
		{interval}
		on:cancel={handleCancelModal}
		on:save={handleSaveInterval}
	/>
{/if}

<IconSearchModal
	showModal={showIconSearchModal}
	on:close={() => (showIconSearchModal = false)}
	on:icon-selected={handleIconSelected}
/>

<style>
	.scene-metadata-inspector {
		padding: 1rem;
		height: 100%;
		overflow-y: auto;
	}

	:global(.flow-meta-top-row) {
		flex-wrap: wrap;
		width: 100%;
	}

	.flow-icon-setting {
		display: flex;
		flex-direction: column;
		gap: 6px;
		min-width: 220px;
	}

	.flow-icon-label {
		font-size: 12px;
		color: #9ca3af;
	}

	.flow-icon-controls {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.flow-icon-preview {
		width: 38px;
		height: 38px;
		flex: 0 0 38px;
		display: grid;
		place-items: center;
		border-radius: 10px;
		color: white;
		box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.18);
	}

	.inspector-header {
		margin-bottom: 0.5rem;
		padding-bottom: 0.75rem;
		border-bottom: 1px solid rgba(255, 255, 255, 0.1);
	}

	.inspector-header h3 {
		margin: 0;
		font-size: 1.125rem;
		font-weight: 600;
		color: #fff;
	}

	.scene-metadata-inspector.embedded {
		padding: 0;
		height: auto;
		overflow: visible;
	}
</style>
